import { createPortal } from 'react-dom';
import { useLayoutEffect, useRef } from 'react';

export type DashboardBreakdownTooltipProps = {
  lines: string[];
  anchor: { x: number; y: number } | null;
  className?: string;
};

/** Fixed-position breakdown tooltip (pie slice / legend hover). */
export function DashboardBreakdownTooltip({
  lines,
  anchor,
  className = 'dash-chart-breakdown-tip',
}: DashboardBreakdownTooltipProps) {
  const tipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!anchor || !tipRef.current) return;

    const margin = 8;
    const el = tipRef.current;
    el.style.left = `${anchor.x}px`;
    el.style.top = `${anchor.y - 8}px`;
    el.style.transform = 'translate(-50%, -100%)';

    const rect = el.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - margin);
    const overflowLeft = margin - rect.left;
    const overflowTop = margin - rect.top;

    let x = anchor.x;
    let y = anchor.y - 8;
    let transform = 'translate(-50%, -100%)';

    if (overflowRight > 0) x -= overflowRight;
    else if (overflowLeft > 0) x += overflowLeft;

    if (overflowTop > 0) {
      y = anchor.y + 16;
      transform = 'translate(-50%, 0)';
    }

    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.transform = transform;
  }, [anchor, lines]);

  if (!anchor || lines.length === 0) return null;

  return createPortal(
    <div ref={tipRef} className={`${className} is-open`} role="tooltip">
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>,
    document.body,
  );
}

export function sliceBreakdownLines(slice: { label: string; value: number; breakdown?: string[] }, total: number): string[] {
  if (slice.breakdown?.length) return slice.breakdown;
  const pct = total > 0 ? Math.round((slice.value / total) * 100) : 0;
  return [`${slice.label}: ${pct}% (${slice.value.toLocaleString()})`];
}
